import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';

const UnstampedPacks = () => {
  // Get current date and calculate last month
  const getDefaultDate = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    let lastMonth = currentMonth - 1;
    let year = currentYear;
    
    if (lastMonth < 0) {
      lastMonth = 11;
      year = currentYear - 1;
    }
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    return {
      month: monthNames[lastMonth],
      year: year.toString()
    };
  };

  const defaultDate = getDefaultDate();

  // Available pack types
  const packTypes = ['20s', '25s'];

  const [selectedPackType, setSelectedPackType] = useState('20s');
  const [formData, setFormData] = useState({
    packs: {
      '20s': {
        line28: '',
        manufacturers: [
          { id: 1, name: '', packs: '' }
        ],
        line31: '',
        line32: '',
        line33: '',
        line34: '',
        adjustmentExplanation: ''
      },
      '25s': {
        line28: '',
        manufacturers: [
          { id: 1, name: '', packs: '' }
        ],
        line31: '',
        line32: '',
        line33: '',
        line34: '',
        adjustmentExplanation: ''
      }
    }
  });

  const [loading, setLoading] = useState({
    '20s': false,
    '25s': false
  });

  const [submitting, setSubmitting] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');
  const [attachments, setAttachments] = useState([]);

  // NPM Brands State (for manufacturer dropdown)
  const [npmBrands, setNpmBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // Get taxpayer ID and business name from localStorage
  const [taxpayerId, setTaxpayerId] = useState('');
  const [businessName, setBusinessName] = useState('');

  // Distributor Information State
  const [distributorInfo, setDistributorInfo] = useState({
    distributor: '',
    month: defaultDate.month,
    year: defaultDate.year,
    expirationDate: ''
  });

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());

  // State to track if line28 is editable - only make it read-only when API returns non-zero value
  const [isLine28Editable, setIsLine28Editable] = useState({
    '20s': true,
    '25s': true
  });

  // State to track if the value was manually modified
  const [isLine28ManuallyModified, setIsLine28ManuallyModified] = useState({
    '20s': false,
    '25s': false
  });

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    const storedExpirationDate = localStorage.getItem("businessLicenseExpirationDate");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
    } else {
      setTaxpayerId('109');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      setDistributorInfo(prev => ({
        ...prev,
        distributor: storedBusinessName
      }));
    } else {
      setDistributorInfo(prev => ({
        ...prev,
        distributor: 'Black Canyon Distribution, LLC - PBPN License #41112-7'
      }));
    }

    if (storedExpirationDate) {
      setDistributorInfo(prev => ({
        ...prev,
        expirationDate: storedExpirationDate
      }));
    }
  }, []);

  // Fetch NPM brands when taxpayerId is available
  useEffect(() => {
    if (taxpayerId) {
      fetchNpmBrands();
    }
  }, [taxpayerId]);

  const fetchNpmBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await fetch(`${PBPUTILS}?taxpayer=${taxpayerId}&action=getNPMs`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        console.log('Fetched NPM brands:', data.data);
        setNpmBrands(data.data || []);
      } else {
        console.error('Failed to fetch NPM brands:', data.message);
        setNpmBrands([]);
      }
    } catch (error) {
      console.error('Error fetching NPM brands:', error);
      setNpmBrands([]);
    } finally {
      setLoadingBrands(false);
    }
  };

  // Format month for API
  const formatMonthForAPI = (monthName) => {
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return monthMap[monthName] || '01';
  };

  // Fetch starting count from API
  const fetchStartingCount = async (packType) => {
    if (!taxpayerId) return;

    try {
      setLoading(prev => ({ ...prev, [packType]: true }));

      const formattedMonth = formatMonthForAPI(distributorInfo.month);
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&date=${formattedMonth}-${distributorInfo.year}&reporttype=Unstamped&action=getStartCount&perpack=${packType}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();
      const count = parseInt(data.trim(), 10);

      if (!isNaN(count)) {
        setFormData(prev => ({
          ...prev,
          packs: {
            ...prev.packs,
            [packType]: {
              ...prev.packs[packType],
              line28: count.toString()
            }
          }
        }));
        // Only make it read-only if API returns a non-zero value AND it hasn't been manually modified
        if (count !== 0 && !isLine28ManuallyModified[packType]) {
          setIsLine28Editable(prev => ({
            ...prev,
            [packType]: false
          }));
        } else {
          setIsLine28Editable(prev => ({
            ...prev,
            [packType]: true
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          packs: {
            ...prev.packs,
            [packType]: {
              ...prev.packs[packType],
              line28: '0'
            }
          }
        }));
        setIsLine28Editable(prev => ({
          ...prev,
          [packType]: true
        }));
      }
    } catch (error) {
      console.error(`Error fetching starting count for ${packType}:`, error);
      setFormData(prev => ({
        ...prev,
        packs: {
          ...prev.packs,
          [packType]: {
            ...prev.packs[packType],
            line28: '0'
          }
        }
      }));
      setIsLine28Editable(prev => ({
        ...prev,
        [packType]: true
      }));
    } finally {
      setLoading(prev => ({ ...prev, [packType]: false }));
    }
  };

  // Fetch starting count when month, year, pack type, or taxpayer ID changes
  useEffect(() => {
    if (taxpayerId) {
      fetchStartingCount(selectedPackType);
    }
  }, [distributorInfo.month, distributorInfo.year, selectedPackType, taxpayerId]);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Handle input changes for regular fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      packs: {
        ...prev.packs,
        [selectedPackType]: {
          ...prev.packs[selectedPackType],
          [name]: value
        }
      }
    }));

    // Mark line28 as manually modified when user types in it
    if (name === 'line28') {
      setIsLine28ManuallyModified(prev => ({
        ...prev,
        [selectedPackType]: true
      }));
      // Always keep it editable if manually modified
      setIsLine28Editable(prev => ({
        ...prev,
        [selectedPackType]: true
      }));
    }
  };

  // Handle manufacturer field changes
  const handleManufacturerChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      packs: {
        ...prev.packs,
        [selectedPackType]: {
          ...prev.packs[selectedPackType],
          manufacturers: prev.packs[selectedPackType].manufacturers.map(manufacturer =>
            manufacturer.id === id ? { ...manufacturer, [field]: value } : manufacturer
          )
        }
      }
    }));
  };

  // Add new manufacturer field
  const addManufacturer = () => {
    const currentManufacturers = getCurrentPackData().manufacturers;
    const newId = Math.max(...currentManufacturers.map(m => m.id), 0) + 1;
    
    setFormData(prev => ({
      ...prev,
      packs: {
        ...prev.packs,
        [selectedPackType]: {
          ...prev.packs[selectedPackType],
          manufacturers: [
            ...prev.packs[selectedPackType].manufacturers,
            { id: newId, name: '', packs: '' }
          ]
        }
      }
    }));
  };

  // Remove manufacturer field
  const removeManufacturer = (id) => {
    const currentManufacturers = getCurrentPackData().manufacturers;
    if (currentManufacturers.length <= 1) return;
    
    setFormData(prev => ({
      ...prev,
      packs: {
        ...prev.packs,
        [selectedPackType]: {
          ...prev.packs[selectedPackType],
          manufacturers: prev.packs[selectedPackType].manufacturers.filter(m => m.id !== id)
        }
      }
    }));
  };

  // Handle distributor information changes
  const handleDistributorInfoChange = (field, value) => {
    setDistributorInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get current pack data
  const getCurrentPackData = () => {
    return formData.packs[selectedPackType] || {};
  };

  // Calculate Line 29 as sum of all manufacturer packs
  const calculateLine29 = () => {
    const data = getCurrentPackData();
    return data.manufacturers.reduce((sum, manufacturer) => {
      return sum + (parseFloat(manufacturer.packs) || 0);
    }, 0);
  };

  const calculateLine30 = () => {
    const data = getCurrentPackData();
    const line28 = parseFloat(data.line28) || 0;
    const line29 = calculateLine29();
    return line28 + line29;
  };

  const calculateTotalRemoved = () => {
    const data = getCurrentPackData();
    const line31 = parseFloat(data.line31) || 0;
    const line32 = parseFloat(data.line32) || 0;
    const line33 = parseFloat(data.line33) || 0;
    const line34 = parseFloat(data.line34) || 0;

    return line31 + line32 + line33 + line34;
  };

  const calculateLine35 = () => {
    const line30 = calculateLine30();
    const totalRemoved = calculateTotalRemoved();
    return line30 - totalRemoved;
  };

  // Format number to avoid NaN display
  const formatNumber = (value) => {
    return isNaN(value) ? 0 : value;
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // File upload handlers
  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setAttachments((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = files.filter((f) => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });

    // Allow re-selecting the same file names again later
    event.target.value = '';
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Generate PDF function with signature and proper labels - accepts optional signature override (use when generating for send, since setState is async)
const generatePDF = (signatureDataURLOverride) => {
  const pdf = new jsPDF();
  const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;
  const currentData = getCurrentPackData();
  
  // Set font and colors
  pdf.setFont('helvetica');
  pdf.setTextColor(0, 0, 0);
  
  // Header Section
  pdf.setFontSize(16);
  pdf.text('Monthly Distributor Report', 105, 20, { align: 'center' });
  
  pdf.setFontSize(12);
  pdf.text('Prairie Band Potawatomi Tax Commission', 105, 30, { align: 'center' });
  pdf.text('16281 Q Road', 105, 36, { align: 'center' });
  pdf.text('Mayetta, KS 66509', 105, 42, { align: 'center' });
  
  pdf.setFontSize(10);
  pdf.text('Reports and Payment due on the 10th of each Month', 105, 50, { align: 'center' });
  
  // Distributor Information
  pdf.setFontSize(12);
  pdf.setTextColor(0, 0, 0);
  pdf.text('Distributor Information:', 20, 65);
  pdf.setFontSize(10);
  pdf.text(`Distributor: ${distributorInfo.distributor}`, 20, 72);
  pdf.text(`Month: ${distributorInfo.month} ${distributorInfo.year}`, 20, 78);
  pdf.text(`PBPN Tribal Distributor License Expiration Date: ${distributorInfo.expirationDate}`, 20, 84);
  
  if (taxpayerId) {
    pdf.text(`Taxpayer ID: ${taxpayerId}`, 20, 90);
  }
  
  // Pack Type Information
  pdf.text(`Pack Type: ${selectedPackType} packs`, 20, 96.5); // Reduced spacing
  
  // Unstamped Packs Section
  pdf.setFontSize(12);
  pdf.setTextColor(0, 0, 0);
  pdf.text('PBPN/KS Compact Distributor Information - UNSTAMPED Packs of Cigarettes', 20, 110); // Reduced spacing
  
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  let yPosition = 120; // Reduced starting position
  
  // Unstamped Packs Data with proper labels
  const unstampedData = [
    { 
      label: '28. First of month physical inventory of UNSTAMPED cigarette packs (should equal line 35 from last month)', 
      value: currentData.line28 || '0' 
    },
    { 
      label: '29. # of Unstamped cigarette packs purchased from Wholesalers, Distributers, Importers, Manufacturers', 
      value: formatNumber(calculateLine29()).toString() 
    },
  ];

  // Add manufacturer details with proper labels
  currentData.manufacturers.forEach((manufacturer, index) => {
    unstampedData.push({
      label: `   ${String.fromCharCode(97 + index)}. ${manufacturer.name || 'Manufacturer'}`,
      value: manufacturer.packs || '0'
    });
  });

  // Add remaining lines with proper labels
  unstampedData.push(
    { 
      label: '30. Total unstamped cigarette packs available (Line 28 + Line 29)', 
      value: formatNumber(calculateLine30()).toString() 
    },
    { 
      label: '31. Cigarette packs stamped with PBPN/KS STAMPs (Line 4)', 
      value: currentData.line31 || '0' 
    },
    { 
      label: '32. Cigarette packs stamped with "Other Off-reservation" STAMPs', 
      value: currentData.line32 || '0' 
    },
    { 
      label: '33. # of Unstamped cigarette packs destroyed by process; or returned to Importer', 
      value: currentData.line33 || '0' 
    },
    { 
      label: '34. Adjustment', 
      value: currentData.line34 || '0' 
    },
    { 
      label: '34. Total # of cigarette packs removed from Unstamped Inventory (Line 31 + Line 32 + Line 33 + Line 34)', 
      value: formatNumber(calculateTotalRemoved()).toString() 
    },
    { 
      label: '35. End of month physical inventory of UNSTAMPED cigarette packs (Line 30 - Line 34)', 
      value: formatNumber(calculateLine35()).toString() 
    }
  );
  
  const formatAmount = (value) => {
    if (value === null || value === undefined) return '0';
    const s = String(value).replace(/,/g, '').trim();
    if (s === '') return '0';
    const num = Number(s);
    if (isNaN(num)) return String(value);
    if (s.includes('.')) {
      const decimals = s.split('.')[1].length;
      return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    return num.toLocaleString();
  };

  // Display all data with very compact spacing
  unstampedData.forEach((item) => {
    if (yPosition > 270) {
      pdf.addPage();
      yPosition = 20;
    }
    
    // Split long labels into multiple lines
    const labelLines = pdf.splitTextToSize(item.label, 120);
    const valueLines = pdf.splitTextToSize(formatAmount(item.value), 50);
    
    const maxLines = Math.max(labelLines.length, valueLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      
      if (labelLines[i]) {
        pdf.text(labelLines[i], 20, yPosition);
      }
      
      if (valueLines[i]) {
        pdf.text(valueLines[i], 180, yPosition, { align: 'right' });
      }
      
      yPosition += 5; // Very compact line spacing (from 6 to 4)
    }
    
    // No extra space after sections
  });

  // Add adjustment explanation if provided - very compact
  if (currentData.adjustmentExplanation && currentData.adjustmentExplanation.trim() !== '') {
    if (yPosition > 260) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.text('Adjustment Explanation:', 20, yPosition);
    yPosition += 4; // Minimal gap
    
    const explanationLines = pdf.splitTextToSize(currentData.adjustmentExplanation, 170);
    explanationLines.forEach((line) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, 25, yPosition);
      yPosition += 4; // Compact line spacing
    });
    // No extra space after explanation
  }
  
  // FIXED: Much more aggressive space management
  // Calculate exactly how much space we have left
  const spaceLeft = 280 - yPosition;
  const signatureSectionHeight = 60; // Reduced estimated height
  
  if (spaceLeft < signatureSectionHeight + 10) {
    // If not enough space, start new page
    pdf.addPage();
    yPosition = 20;
  } else {
    // If we have space, use VERY small gap - start signature immediately
    yPosition += 2; // Almost no gap - signature starts right after content
  }
  
  // Signature Section - Ultra compact layout
  pdf.setFontSize(11); // Slightly smaller title
  pdf.setTextColor(0, 0, 0);
  pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
  yPosition += 6; // Very compact spacing

  // Certification Text as single compact block
  pdf.setFontSize(8); // Smaller font for certification
  const certificationText = 'I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.';
  const certLines = pdf.splitTextToSize(certificationText, 170);
  
  certLines.forEach(line => {
    pdf.text(line, 20, yPosition);
    yPosition += 3.5; // Very tight line spacing
  });
  
  yPosition += 4; // Small gap after certification

  // Signature area - compact
  if (signatureToUse) {
    try {
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Signature:', 20, yPosition);
      yPosition += 3; // Minimal gap
      
      // Compact signature image
      const signatureWidth = 60;
      const signatureHeight = 20;
      
      pdf.addImage(signatureToUse, 'PNG', 20, yPosition, signatureWidth, signatureHeight);
      yPosition += signatureHeight + 4; // Compact spacing
    } catch (error) {
      pdf.setFontSize(8);
      pdf.text('Signature: Provided during submission', 20, yPosition);
      yPosition += 4;
    }
  } else {
    pdf.setFontSize(8);
    pdf.text('Signature: Not provided', 20, yPosition);
    yPosition += 4;
  }

  // Printed info in compact grid
  pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
  pdf.text(`Title: ${title}`, 110, yPosition); // Same line, right side
  yPosition += 4;
  
  pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);
  yPosition += 6;
  
  // Footer information at very bottom
  const finalY = Math.min(yPosition, 280); // Don't go beyond page
  pdf.setFontSize(6); // Very small footer
  pdf.setTextColor(0, 0, 0);
  
  return pdf;
};

  const formDataToObject = (formData) => {
    const object = {};
    for (const [key, value] of formData.entries()) {
      // Handle array fields (manufacturer[] and packs[])
      if (key.endsWith('[]')) {
        const baseKey = key.slice(0, -2);
        if (!object[baseKey]) {
          object[baseKey] = [];
        }
        object[baseKey].push(value);
      } 
      // Handle JSON fields (like received)
      else if (key === 'received') {
        try {
          object[key] = JSON.parse(value);
        } catch (e) {
          object[key] = value;
        }
      }
      else {
        object[key] = value;
      }
    }
    return object;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `Unstamped_Report_${distributorInfo.month}_${distributorInfo.year}_${selectedPackType}_${taxpayerId || 'unknown'}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
  };

  // Updated handleSubmit function with beginningInventory and adjustmentExplanation
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    try {
      setSubmitting(true);

      // Get signature data and store it for PDF generation
      const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(sigDataURL);

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(sigDataURL);

      // Convert PDF to Blob and then to File
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `Unstamped_Report_${distributorInfo.month}_${distributorInfo.year}_${selectedPackType}_${taxpayerId || 'unknown'}.pdf`, {
        type: 'application/pdf'
      });

      // Get current pack data
      const currentData = getCurrentPackData();
      
      // Format date for API
      const formattedMonth = formatMonthForAPI(distributorInfo.month);
      const dateParam = `${formattedMonth}-${distributorInfo.year}`;
      
      // Prepare form data according to API requirements
      const formDataToSend = new FormData();
      
      // Add URL parameters as form data
      formDataToSend.append('taxpayer', taxpayerId);
      formDataToSend.append('reporttype', 'unstamped');
      formDataToSend.append('date', dateParam);
      
      // Add report month and year
      formDataToSend.append('reportMonth', distributorInfo.month);
      formDataToSend.append('reportYear', distributorInfo.year);
      
      // Prepare the Body data in the required JSON format
      const bodyData = {
        received: currentData.manufacturers.map(manufacturer => ({
          manufacturer: manufacturer.name || '',
          packs: parseInt(manufacturer.packs) || 0
        })),
        line31: parseInt(currentData.line31) || 0,
        line32: parseInt(currentData.line32) || 0,
        line33: parseInt(currentData.line33) || 0,
        line34: parseInt(currentData.line34) || 0,
        beginningInventory: parseInt(currentData.line28) || 0,
        adjustmentExplanation: currentData.adjustmentExplanation || ''
      };

      // Add Body as JSON string
      formDataToSend.append('Body', JSON.stringify(bodyData));
      
      // Add printed name and title
      formDataToSend.append('printedName', printedName);
      formDataToSend.append('title', title);
      
      // Add signature
      const signatureFile = dataURLtoFile(sigDataURL, 'signature.png');
      formDataToSend.append('Signature', signatureFile);

      // Append PDF to FormData
      formDataToSend.append('PDF', pdfFile);

      // Append any optional supporting document uploads
      attachments.forEach((file, index) => {
        formDataToSend.append('Receipts', file);
        // Append file type with index to match files with their types
        // formDataToSend.append(`ReceiptsType_${index}`, 'Invoice');
      });
      console.log('Number of selected files:', attachments.length);  
      
      // Store submission data for PDF generation
      setSubmissionData({
        distributorInfo,
        currentData,
        selectedPackType,
        taxpayerId,
        businessName,
        printedName,
        title,
        calculations: {
          line29: calculateLine29(),
          line30: calculateLine30(),
          totalRemoved: calculateTotalRemoved(),
          line35: calculateLine35()
        }
      });

      const formDataObject = formDataToObject(formDataToSend);
      console.log('FormData contents:', JSON.stringify(formDataObject, null, 2));
      
      // Make API call
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&reporttype=unstamped&date=${formattedMonth}-${distributorInfo.year}`;

      console.log('API URL:', apiUrl);
      console.log('Body data being sent:', bodyData);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });
      console.log(response)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseText = await response.text();
      let result;
      result = JSON.parse(responseText);
      console.log('API Response:', responseText);
      
      if (result && result.Status === 'Success') {
        setShowSuccessScreen(true);
        return;
      } else if (result && result.Status === 'Error') {
        alert(result.Message || 'An error occurred while submitting the report.');
        return;
      } else {
        throw new Error('Unexpected response format from server');
      }
      
    } catch (error) {
      console.error('Error submitting report:', error);
      alert(`Error submitting report: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Unstamped Packs Inventory',
    'Pack Type': `${selectedPackType} packs`,
    'Taxpayer ID': taxpayerId,
    'Business': businessName,
    'Period': `${distributorInfo.month} ${distributorInfo.year}`,
    'Submitted': currentDate,
    // 'Distributor': distributorInfo.distributor
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Unstamped Packs Report Submitted Successfully!"
        subtitle={`Your unstamped packs report for ${selectedPackType} packs has been successfully submitted.`}
        downloadButtonText="Download Unstamped Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• The report covers unstamped packs inventory for the specified period</li>
            <li>• Ensure all manufacturer details are accurately recorded</li>
            <li>• Contact us if you need to make any corrections</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  const currentData = getCurrentPackData();

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header Section */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">Monthly Distributor Report</h1>
        <div className="font-bold mb-2 text-[#840028] text-sm sm:text-base">
          <div>Prairie Band Potawatomi Tax Commission</div>
          <div>16281 Q Road</div>
          <div>Mayetta, KS 66509</div>
        </div>
        <div className="italic mb-2 text-[#840028] text-sm sm:text-base">
          Reports and Payment due on the 10th of each Month
        </div>
        {taxpayerId && (
          <div className="text-xs sm:text-sm text-gray-600 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-4">
            <span>Taxpayer ID: <strong>{taxpayerId}</strong></span>
            {businessName && (
              <span>Business: <strong>{businessName}</strong></span>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Distributor Information Section */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6">
          {/* Distributor Row - At Top */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Distributor:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 text-sm sm:text-base">
              {distributorInfo.distributor}
            </div>
          </div>

          {/* Month and Year Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
            <select
              value={distributorInfo.month}
              onChange={(e) => handleDistributorInfoChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
            >
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>

            <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
            <select
              value={distributorInfo.year}
              onChange={(e) => handleDistributorInfoChange('year', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="hidden">
            <input
              type="text"
              value={distributorInfo.reportMonth}
              readOnly
            />
            <input
              type="text"
              value={distributorInfo.reportYear}
              readOnly
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">PBPN Tribal Distributor License Expiration Date:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-32 text-sm sm:text-base">
              {distributorInfo.expirationDate}
            </div>
          </div>
        </div>

        {/* Pack Type Selection */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6 bg-blue-50">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Select Pack Type:</label>
            <select
              value={selectedPackType}
              onChange={(e) => setSelectedPackType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 text-sm sm:text-base"
            >
              {packTypes.map(packType => (
                <option key={packType} value={packType}>{packType} packs</option>
              ))}
            </select>
            <span className="mt-2 sm:mt-0 sm:ml-4 text-sm text-gray-600 text-center sm:text-left">
              Currently reporting for <strong>{selectedPackType}</strong> packs
            </span>
          </div>
        </div>

        {/* Main Form Content */}
        <div className="p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-800 text-center sm:text-left">
            PBPN/KS Compact Distributor Information - UNSTAMPED Packs of Cigarettes
          </h2>

          {/* Table Header */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="text-sm font-semibold text-gray-700">
              Number of cigarettes per pack --- {selectedPackType}
            </div>
            <div className="text-sm font-semibold text-gray-700 text-center sm:text-right">
              # of Packs {selectedPackType}
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Line 28 - Auto-filled from API - Now editable only when API returns non-zero value AND not manually modified */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                28. First of month physical inventory of UNSTAMPED cigarette packs (should equal line 35 from last month)
                {loading[selectedPackType] && (
                  <span className="ml-2 text-blue-600 text-xs">Loading...</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  name="line28"
                  value={currentData.line28 || ''}
                  onChange={handleInputChange}
                  className={`border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right ${
                    !isLine28Editable[selectedPackType] ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="0"
                  readOnly={!isLine28Editable[selectedPackType]}
                />
                {loading[selectedPackType] && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Line 29 - Calculated from manufacturers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                29. # of Unstamped cigarette packs purchased from Wholesalers, Distributers, Importers, Manufacturers
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border text-sm text-center sm:text-right">
                {formatNumber(calculateLine29())}
              </div>
            </div>

            {/* Dynamic Manufacturer Fields */}
            {currentData.manufacturers.map((manufacturer, index) => (
              <div key={manufacturer.id} className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center ml-0 sm:ml-4">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                  <label className="text-sm text-gray-700 min-w-8">
                    {String.fromCharCode(97 + index)}.
                  </label>
                  <select
                    value={npmBrands.find(brand => brand.dval === manufacturer.name)?.sval || ''}
                    onChange={(e) => {
                      const sval = e.target.value;
                      if (!sval) {
                        handleManufacturerChange(manufacturer.id, 'name', '');
                        return;
                      }
                      const selected = npmBrands.find(brand => brand.sval.toString() === sval);
                      handleManufacturerChange(manufacturer.id, 'name', selected ? selected.dval : '');
                    }}
                    className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 text-sm bg-white"
                  >
                    <option value="">Select Manufacturer</option>
                    {loadingBrands ? (
                      <option value="" disabled>Loading manufacturers...</option>
                    ) : (
                      npmBrands.map((brand) => (
                        <option key={brand.sval} value={brand.sval}>
                          {brand.dval}
                        </option>
                      ))
                    )}
                  </select>
                  {!loadingBrands && npmBrands.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">No manufacturers available</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 ml-0 sm:ml-4">
                  <input
                    type="number"
                    min="0"
                    value={manufacturer.packs}
                    onChange={(e) => handleManufacturerChange(manufacturer.id, 'packs', e.target.value)}
                    className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-32 text-sm text-right"
                    placeholder="0"
                  />
                  {currentData.manufacturers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeManufacturer(manufacturer.id)}
                      className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200 text-xs w-full sm:w-auto"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Add More Manufacturers Button */}
            <div className="ml-0 sm:ml-4">
              <button
                type="button"
                onClick={addManufacturer}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition duration-200 text-sm w-full sm:w-auto"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Another Manufacturer
              </button>
            </div>

            {/* Line 30 - Calculated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center border-t pt-4">
              <label className="text-sm text-gray-700 font-semibold">
                30. Total unstamped cigarette packs available (Line 28 + Line 29)
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border text-sm text-center sm:text-right">
                {formatNumber(calculateLine30())}
              </div>
            </div>

            {/* Line 31 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                31. Cigarette packs stamped with PBPN/KS STAMPs (Line 4)
              </label>
              <input
                type="number"
                min="0"
                name="line31"
                value={currentData.line31 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 32 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                32. Cigarette packs stamped with "Other Off-reservation" STAMPs
              </label>
              <input
                type="number"
                min="0"
                name="line32"
                value={currentData.line32 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 33 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                33. # of Unstamped cigarette packs destroyed by process; or returned to Importer
              </label>
              <input
                type="number"
                name="line33"
                min="0"
                value={currentData.line33 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 34 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <div>
                <label className="text-sm text-gray-700 block mb-1">
                  34. Adjustment (Please explain)
                </label>
                <textarea
                  name="adjustmentExplanation"
                  value={currentData.adjustmentExplanation || ''}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows="2"
                  placeholder="Enter explanation for adjustment..."
                />
              </div>
              <input
                type="number"
                name="line34"
                min="0"
                value={currentData.line34 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 34 Total - Calculated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                34. Total # of cigarette packs removed from Unstamped Inventory (Line 31 + Line 32 + Line 33 + Line 34)
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border text-sm text-center sm:text-right">
                {formatNumber(calculateTotalRemoved())}
              </div>
            </div>

            {/* Line 35 - Calculated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center border-t pt-4">
              <label className="text-sm text-gray-700 font-semibold">
                35. End of month physical inventory of UNSTAMPED cigarette packs (Line 30 - Line 34)
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border font-semibold text-sm text-center sm:text-right">
                {formatNumber(calculateLine35())}
              </div>
            </div>
          </div>

          {/* Supporting Documents (Optional) */}
          <div className="border border-gray-300 p-4 sm:p-6 mt-6 sm:mt-8 mb-6 rounded-lg bg-white">
            <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">
              Supporting Documents (Optional)
            </h3>
            <p className="text-sm text-gray-700 mb-3 text-center">
              You may upload one or more files to support this report. This step is optional.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Files:
              </label>
              <input
                type="file"
                multiple
                onChange={handleAttachmentChange}
                className="block w-full text-sm text-gray-700
                           file:mr-4 file:py-2 file:px-4
                           file:rounded-md file:border-0
                           file:text-sm file:font-semibold
                           file:bg-[#840028] file:text-white
                           hover:file:bg-[#6a0020]"
              />
              {attachments && attachments.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Selected files:</p>
                  <ul className="space-y-2 text-xs text-gray-700">
                    {attachments.map((file, index) => (
                      <li
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1"
                      >
                        <span className="truncate mr-2">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="text-red-600 hover:text-red-800 text-[11px] font-semibold"
                        >
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Signature Section */}
          <div className="border border-gray-300 p-4 sm:p-6 mb-6 rounded-lg">
            <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

            <div className="mb-4">
              <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
                Sign below to certify the accuracy of this report:
              </label>
              <div
                id="signature-container"
                className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
              >
                {canvasSize.width > 0 && (
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="#0000FF"
                    backgroundColor="rgb(255, 255, 255)"
                    canvasProps={{
                      width: canvasSize.width,
                      height: canvasSize.height,
                      className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                    }}
                    onBegin={handleSignatureBegin}
                    onEnd={handleSignatureEnd}
                    clearOnResize={false}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <div className="text-sm text-gray-600 text-center sm:text-left">
                {isSigned ? (
                  <span className="text-green-600 font-bold">✓ Signature provided</span>
                ) : (
                  <span className="text-red-600">Please sign above</span>
                )}
              </div>

              <button
                type="button"
                onClick={clearSignature}
                className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm w-full sm:w-auto"
              >
                Clear Signature
              </button>
            </div>

            {/* Printed Name and Title Fields - Added */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Printed Name: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={printedName}
                    onChange={(e) => setPrintedName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter your title"
                    required
                  />
                </div>
              </div>

              {/* Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Signed:
                </label>
                <input
                  type="text"
                  value={currentDate}
                  readOnly
                  className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This date is automatically set to today's date and cannot be modified
                </p>
              </div>
            </div>
          </div>

          {/* Certification */}
          <div className="border-t-2 border-b-2 border-[#840028] py-4 sm:py-6 my-6 sm:my-8 text-center italic text-[#840028] text-sm sm:text-base">
            <p>
              I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
            </p>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={!isSigned || submitting || !printedName.trim() || !title.trim()}
              className="bg-[#840028] hover:bg-[#6a0020] text-white font-bold py-3 px-6 sm:px-8 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                isSigned && printedName.trim() && title.trim() ? 'Submit Report' : 'Please Complete All Required Fields'
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 200px;
        }
        
        @media (max-width: 640px) {
          #signature-container {
            min-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default UnstampedPacks;